import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Calculator, RefreshCw, Save } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const AgencyTariffSettings = () => {
  const [tiers, setTiers] = useState([]);
  const [settings, setSettings] = useState({
    extended_stay_threshold_days: 7,
    extended_stay_period_days: 5,
    extended_stay_surcharge_percent: 20
  });
  const [loading, setLoading] = useState(true);
  const [showTierModal, setShowTierModal] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [tierForm, setTierForm] = useState({
    min_nt: '',
    max_nt: '',
    base_fee_eur: '',
    description: '',
    order: 0
  });
  
  // Hesaplama test state'leri
  const [testNT, setTestNT] = useState('');
  const [testDays, setTestDays] = useState('1');
  const [calculationResult, setCalculationResult] = useState(null);
  const [calculating, setCalculating] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [tiersRes, settingsRes] = await Promise.all([
        api.get('/agency-tariffs/tiers'),
        api.get('/agency-tariffs/settings')
      ]);
      setTiers(tiersRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Agency tariff load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await api.put('/agency-tariffs/settings', settings);
      toast.success('Tarife ayarları kaydedildi');
    } catch (error) {
      toast.error('Ayarlar kaydedilemedi');
    }
  };

  const handleAddTier = () => {
    setEditingTier(null);
    setTierForm({
      min_nt: '',
      max_nt: '',
      base_fee_eur: '',
      description: '',
      order: tiers.length + 1
    });
    setShowTierModal(true);
  };

  const handleEditTier = (tier) => {
    setEditingTier(tier);
    setTierForm({
      min_nt: tier.min_nt || '',
      max_nt: tier.max_nt || '',
      base_fee_eur: tier.base_fee_eur || '',
      description: tier.description || '',
      order: tier.order || 0
    });
    setShowTierModal(true);
  };

  const handleDeleteTier = async (tierId) => {
    if (!window.confirm('Bu tarife kademesini silmek istediğinizden emin misiniz?')) return;
    
    try {
      await api.delete(`/agency-tariffs/tiers/${tierId}`);
      toast.success('Tarife kademesi silindi');
      loadData();
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const handleSaveTier = async () => {
    if (!tierForm.min_nt || !tierForm.base_fee_eur) {
      toast.error('Min NT ve Baz Ücret zorunludur');
      return;
    }

    const data = {
      min_nt: parseFloat(tierForm.min_nt),
      max_nt: tierForm.max_nt ? parseFloat(tierForm.max_nt) : null,
      base_fee_eur: parseFloat(tierForm.base_fee_eur),
      description: tierForm.description || `${tierForm.min_nt}-${tierForm.max_nt || '+'} NT`,
      order: parseInt(tierForm.order) || 0,
      active: true
    };

    try {
      if (editingTier) {
        await api.put(`/agency-tariffs/tiers/${editingTier.id}`, data);
        toast.success('Tarife kademesi güncellendi');
      } else {
        await api.post('/agency-tariffs/tiers', data);
        toast.success('Tarife kademesi eklendi');
      }
      setShowTierModal(false);
      loadData();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleSeedDefaults = async () => {
    if (!window.confirm('Örnek tarife kademeleri oluşturulsun mu? (Mevcut boşsa)')) return;
    
    try {
      await api.post('/agency-tariffs/seed-defaults');
      toast.success('Varsayılan tarifeler oluşturuldu');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleCalculate = async () => {
    if (!testNT) {
      toast.error('Net Tonaj giriniz');
      return;
    }

    setCalculating(true);
    try {
      const response = await api.post('/agency-tariffs/calculate', {
        net_tonnage: parseFloat(testNT),
        stay_days: parseInt(testDays) || 1
      });
      setCalculationResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Hesaplama başarısız');
      setCalculationResult(null);
    } finally {
      setCalculating(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '2rem' }}>Yükleniyor...</div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-body">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div>
              <h3 style={{ margin: 0, color: '#10b981' }}>💰 Acentelik Tarifesi</h3>
              <p style={{ margin: '0.5rem 0 0 0', color: '#64748b', fontSize: '0.875rem' }}>
                Gemi Net Tonajına (NT) göre acentelik ücretleri. Tarife EUR cinsindendir, proformada USD'ye çevrilir.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {tiers.length === 0 && (
                <button className="btn btn-secondary" onClick={handleSeedDefaults}>
                  <RefreshCw size={16} />
                  Örnek Yükle
                </button>
              )}
              <button className="btn btn-primary" onClick={handleAddTier}>
                <Plus size={16} />
                Kademe Ekle
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tariff Tiers Table */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header" style={{ background: '#f0fdf4', borderBottom: '2px solid #10b981' }}>
          <h4 style={{ margin: 0, color: '#166534' }}>Tarife Kademeleri</h4>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {tiers.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
              Henüz tarife kademesi tanımlanmamış. "Örnek Yükle" veya "Kademe Ekle" butonlarını kullanın.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Sıra</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Net Tonaj Aralığı</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>Baz Ücret (EUR)</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Açıklama</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {tiers.map((tier, index) => (
                  <tr key={tier.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <span style={{ 
                        background: '#10b981', 
                        color: 'white', 
                        padding: '0.25rem 0.5rem', 
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: 600
                      }}>
                        {tier.order || index + 1}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem 1rem', fontWeight: 500 }}>
                      {tier.min_nt} - {tier.max_nt !== null ? tier.max_nt : '∞'} NT
                    </td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: 600, color: '#059669' }}>
                      €{tier.base_fee_eur?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', color: '#64748b' }}>
                      {tier.description}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                        <button 
                          className="action-btn action-btn-edit" 
                          onClick={() => handleEditTier(tier)}
                          title="Düzenle"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button 
                          className="action-btn action-btn-danger" 
                          onClick={() => handleDeleteTier(tier.id)}
                          title="Sil"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Extended Stay Settings */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header" style={{ background: '#fef3c7', borderBottom: '2px solid #f59e0b' }}>
          <h4 style={{ margin: 0, color: '#92400e' }}>⏱️ Uzun Kalış Ek Ücreti</h4>
        </div>
        <div className="card-body">
          <p style={{ color: '#64748b', marginBottom: '1rem', fontSize: '0.875rem' }}>
            Belirlenen gün sayısından fazla kalış durumunda, her dönem için baz ücrete yüzdelik ek ücret eklenir.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Eşik Süresi (Gün)</label>
              <input
                type="number"
                className="form-input"
                value={settings.extended_stay_threshold_days || 7}
                onChange={(e) => setSettings({ ...settings, extended_stay_threshold_days: parseInt(e.target.value) || 7 })}
                min="1"
              />
              <small style={{ color: '#64748b' }}>Bu günden sonra ek ücret başlar</small>
            </div>
            <div className="form-group">
              <label className="form-label">Dönem Süresi (Gün)</label>
              <input
                type="number"
                className="form-input"
                value={settings.extended_stay_period_days || 5}
                onChange={(e) => setSettings({ ...settings, extended_stay_period_days: parseInt(e.target.value) || 5 })}
                min="1"
              />
              <small style={{ color: '#64748b' }}>Her kaç günlük dönem için</small>
            </div>
            <div className="form-group">
              <label className="form-label">Ek Ücret Oranı (%)</label>
              <input
                type="number"
                className="form-input"
                value={settings.extended_stay_surcharge_percent || 20}
                onChange={(e) => setSettings({ ...settings, extended_stay_surcharge_percent: parseFloat(e.target.value) || 20 })}
                min="0"
                step="0.1"
              />
              <small style={{ color: '#64748b' }}>Her dönem için yüzde</small>
            </div>
          </div>
          <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#fef9c3', borderRadius: '6px', fontSize: '0.875rem' }}>
            <strong>Örnek:</strong> {settings.extended_stay_threshold_days || 7} günden fazla kalınması halinde, 
            {settings.extended_stay_threshold_days || 7} günden sonraki her {settings.extended_stay_period_days || 5} günlük 
            dönem ve kesri için baz ücrete %{settings.extended_stay_surcharge_percent || 20} eklenir.
          </div>
          <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-primary" onClick={handleSaveSettings}>
              <Save size={16} />
              Ayarları Kaydet
            </button>
          </div>
        </div>
      </div>

      {/* Calculator */}
      <div className="card">
        <div className="card-header" style={{ background: '#dbeafe', borderBottom: '2px solid #3b82f6' }}>
          <h4 style={{ margin: 0, color: '#1e40af' }}>🧮 Ücret Hesaplama (Test)</h4>
        </div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', alignItems: 'end' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Net Tonaj (NT)</label>
              <input
                type="number"
                className="form-input"
                value={testNT}
                onChange={(e) => setTestNT(e.target.value)}
                placeholder="Örn: 3500"
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Kalış Süresi (Gün)</label>
              <input
                type="number"
                className="form-input"
                value={testDays}
                onChange={(e) => setTestDays(e.target.value)}
                placeholder="Örn: 10"
                min="1"
              />
            </div>
            <button 
              className="btn btn-primary" 
              onClick={handleCalculate}
              disabled={calculating}
              style={{ height: '42px' }}
            >
              <Calculator size={16} />
              {calculating ? 'Hesaplanıyor...' : 'Hesapla'}
            </button>
          </div>

          {calculationResult && (
            <div style={{ 
              marginTop: '1.5rem', 
              padding: '1rem', 
              background: '#f0fdf4', 
              borderRadius: '8px',
              border: '1px solid #bbf7d0'
            }}>
              <h5 style={{ margin: '0 0 1rem 0', color: '#166534' }}>Hesaplama Sonucu</h5>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem', fontSize: '0.875rem' }}>
                <div><strong>Net Tonaj:</strong> {calculationResult.net_tonnage} NT</div>
                <div><strong>Kalış Süresi:</strong> {calculationResult.stay_days} gün</div>
                <div><strong>Kademe:</strong> {calculationResult.tier_description}</div>
                <div><strong>Baz Ücret:</strong> €{calculationResult.base_fee_eur?.toFixed(2)}</div>
                <div><strong>Uzun Kalış Ek Ücreti:</strong> €{calculationResult.extended_stay_surcharge_eur?.toFixed(2)}</div>
                <div><strong>Toplam (EUR):</strong> €{calculationResult.total_fee_eur?.toFixed(2)}</div>
              </div>
              <div style={{ 
                marginTop: '1rem', 
                paddingTop: '1rem', 
                borderTop: '1px solid #bbf7d0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <strong>Toplam (USD):</strong>{' '}
                  <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#059669' }}>
                    ${calculationResult.total_fee_usd?.toFixed(2)}
                  </span>
                  <span style={{ marginLeft: '0.5rem', color: '#64748b', fontSize: '0.75rem' }}>
                    (Kur: {calculationResult.exchange_rate_used})
                  </span>
                </div>
              </div>
              <div style={{ 
                marginTop: '1rem', 
                padding: '0.75rem', 
                background: 'white', 
                borderRadius: '4px',
                fontSize: '0.8rem',
                color: '#64748b',
                whiteSpace: 'pre-line'
              }}>
                {calculationResult.calculation_details}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tier Modal */}
      {showTierModal && (
        <div className="modal-overlay" onClick={() => setShowTierModal(false)}>
          <div className="modal-content" style={{ maxWidth: '500px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editingTier ? 'Kademe Düzenle' : 'Yeni Kademe Ekle'}</h3>
              <button className="modal-close" onClick={() => setShowTierModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Minimum Net Tonaj *</label>
                <input
                  type="number"
                  className="form-input"
                  value={tierForm.min_nt}
                  onChange={(e) => setTierForm({ ...tierForm, min_nt: e.target.value })}
                  placeholder="Örn: 0"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Maksimum Net Tonaj</label>
                <input
                  type="number"
                  className="form-input"
                  value={tierForm.max_nt}
                  onChange={(e) => setTierForm({ ...tierForm, max_nt: e.target.value })}
                  placeholder="Boş bırakılırsa 've üzeri' anlamına gelir"
                />
                <small style={{ color: '#64748b' }}>Boş = sınırsız (ve üzeri)</small>
              </div>
              <div className="form-group">
                <label className="form-label">Baz Ücret (EUR) *</label>
                <input
                  type="number"
                  className="form-input"
                  value={tierForm.base_fee_eur}
                  onChange={(e) => setTierForm({ ...tierForm, base_fee_eur: e.target.value })}
                  placeholder="Örn: 500"
                  step="0.01"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Açıklama</label>
                <input
                  type="text"
                  className="form-input"
                  value={tierForm.description}
                  onChange={(e) => setTierForm({ ...tierForm, description: e.target.value })}
                  placeholder="Örn: 0-500 NT arası gemiler"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Sıra No</label>
                <input
                  type="number"
                  className="form-input"
                  value={tierForm.order}
                  onChange={(e) => setTierForm({ ...tierForm, order: e.target.value })}
                  placeholder="Sıralama için"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowTierModal(false)}>İptal</button>
              <button className="btn btn-primary" onClick={handleSaveTier}>
                <Save size={16} />
                {editingTier ? 'Güncelle' : 'Ekle'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgencyTariffSettings;
